import { normalizeEmail } from '@open-dataroom/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import { Dialog, DialogPanel } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import {
  api,
  ApiError,
  type PermissionsResponse,
  type UsersResponse
} from '~/lib/api';
import { cn } from '~/lib/utils';

type GroupRow = {
  id: string;
  roomId: string | null;
  name: string;
  createdAt: string;
};

type PermissionSubjectType = 'user' | 'group';
type PermissionSubjectKey = `${PermissionSubjectType}:${string}`;
type SharePermission = PermissionsResponse['permissions'][number];

export type ShareAccessTarget =
  | { type: 'room'; id: string; label: string }
  | { type: 'folder'; id: string; label: string };

export interface ShareAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomId: string;
  target: ShareAccessTarget | null;
}

function permissionSubjectKey(
  subjectType: PermissionSubjectType,
  subjectId: string
): PermissionSubjectKey {
  return `${subjectType}:${subjectId}`;
}

function parsePermissionSubjectKey(key: PermissionSubjectKey): {
  subjectType: PermissionSubjectType;
  subjectId: string;
} {
  const separatorIndex = key.indexOf(':');
  return {
    subjectType: key.slice(0, separatorIndex) as PermissionSubjectType,
    subjectId: key.slice(separatorIndex + 1)
  };
}

export function summarizeSharePermissions(
  permissions: SharePermission[],
  target: ShareAccessTarget | null
): {
  viewPermissions: SharePermission[];
  selectedSubjectKeys: Set<PermissionSubjectKey>;
  elevatedPermissionKeys: Set<PermissionSubjectKey>;
  inheritedPermissionKeys: Set<PermissionSubjectKey>;
} {
  const viewPermissions = permissions.filter(
    (permission) =>
      permission.resourceType === target?.type &&
      permission.resourceId === target?.id &&
      (permission.action === 'read' ||
        permission.action === 'write' ||
        permission.action === 'manage') &&
      (permission.subjectType === 'user' || permission.subjectType === 'group')
  );
  const selectedSubjectKeys = new Set<PermissionSubjectKey>();
  const elevatedPermissionKeys = new Set<PermissionSubjectKey>();
  const inheritedPermissionKeys = new Set<PermissionSubjectKey>();

  for (const permission of viewPermissions) {
    const key = permissionSubjectKey(
      permission.subjectType as PermissionSubjectType,
      permission.subjectId
    );
    selectedSubjectKeys.add(key);
    if (permission.action !== 'read') {
      elevatedPermissionKeys.add(key);
    }
    if (permission.inherited) {
      inheritedPermissionKeys.add(key);
    }
  }

  return {
    viewPermissions,
    selectedSubjectKeys,
    elevatedPermissionKeys,
    inheritedPermissionKeys
  };
}

function permissionDetailLabel(input: {
  isElevated: boolean;
  isInherited: boolean;
  inheritedFrom?: SharePermission['inheritedFrom'];
}): string | null {
  if (input.isInherited) {
    const source =
      input.inheritedFrom?.resourceType === 'room'
        ? 'room'
        : input.inheritedFrom?.resourceType === 'folder'
          ? 'parent folder'
          : 'parent';
    return input.isElevated
      ? `Inherited write or manage access from ${source}`
      : `Inherited from ${source}`;
  }

  return input.isElevated ? 'Has write or manage access' : null;
}

export function ShareAccessDialog({
  open,
  onOpenChange,
  roomId,
  target
}: ShareAccessDialogProps) {
  const queryClient = useQueryClient();
  const titleId = React.useId();
  const [selectedSubjectKeys, setSelectedSubjectKeys] = React.useState<
    Set<PermissionSubjectKey>
  >(() => new Set());
  const [permissionsError, setPermissionsError] = React.useState<string | null>(
    null
  );
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteError, setInviteError] = React.useState<string | null>(null);

  const close = React.useCallback(() => {
    onOpenChange(false);
    setSelectedSubjectKeys(new Set());
    setPermissionsError(null);
    setInviteEmail('');
    setInviteError(null);
  }, [onOpenChange]);

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UsersResponse>('/api/users'),
    enabled: open
  });

  const filterGroupsByRoomAccess = target?.type === 'folder';

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: [
      'groups',
      roomId,
      filterGroupsByRoomAccess ? 'with-room-access' : 'all'
    ],
    queryFn: () =>
      api.get<{ groups: GroupRow[] }>(
        `/api/groups?roomId=${encodeURIComponent(roomId)}${
          filterGroupsByRoomAccess ? '&hasRoomAccess=true' : ''
        }`
      ),
    enabled: open
  });

  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions', target?.type, target?.id],
    queryFn: () =>
      api.get<PermissionsResponse>(
        `/api/permissions?resourceType=${encodeURIComponent(target!.type)}&resourceId=${encodeURIComponent(target!.id)}`
      ),
    enabled: open && !!target
  });

  const permissionSummary = React.useMemo(
    () => summarizeSharePermissions(permissionsData?.permissions ?? [], target),
    [permissionsData?.permissions, target?.id, target?.type]
  );
  const { viewPermissions, elevatedPermissionKeys, inheritedPermissionKeys } =
    permissionSummary;

  const inheritedPermissionsByKey = React.useMemo(
    () =>
      new Map(
        viewPermissions
          .filter((permission) => permission.inherited)
          .map((permission) => [
            permissionSubjectKey(
              permission.subjectType as PermissionSubjectType,
              permission.subjectId
            ),
            permission
          ])
      ),
    [viewPermissions]
  );

  const users = React.useMemo(
    () =>
      [...(usersData?.users ?? [])]
        .filter((u) => u.deactivatedAt == null)
        .sort((a, b) => a.email.localeCompare(b.email)),
    [usersData?.users]
  );
  const groups = React.useMemo(
    () =>
      [...(groupsData?.groups ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [groupsData?.groups]
  );

  React.useEffect(() => {
    if (!open || permissionsLoading) return;
    setSelectedSubjectKeys(new Set(permissionSummary.selectedSubjectKeys));
  }, [open, permissionSummary, permissionsLoading]);

  const updatePermissions = useMutation({
    mutationFn: async (input: {
      target: ShareAccessTarget;
      selectedKeys: PermissionSubjectKey[];
      existingPermissions: PermissionsResponse['permissions'];
    }) => {
      const selectedKeySet = new Set(input.selectedKeys);
      const existingEffectivePermissionKeys = new Set(
        input.existingPermissions.map((permission) =>
          permissionSubjectKey(
            permission.subjectType as PermissionSubjectType,
            permission.subjectId
          )
        )
      );
      const readPermissionsByKey = new Map(
        input.existingPermissions
          .filter(
            (permission) =>
              permission.action === 'read' && !permission.inherited
          )
          .map((permission) => [
            permissionSubjectKey(
              permission.subjectType as PermissionSubjectType,
              permission.subjectId
            ),
            permission
          ])
      );

      const createPermissions = input.selectedKeys.filter(
        (key) => !existingEffectivePermissionKeys.has(key)
      );
      const deletePermissions = [...readPermissionsByKey.entries()]
        .filter(([key]) => !selectedKeySet.has(key))
        .map(([, permission]) => permission);

      await Promise.all([
        ...createPermissions.map((key) => {
          const { subjectType, subjectId } = parsePermissionSubjectKey(key);
          return api.post('/api/permissions', {
            resourceType: input.target.type,
            resourceId: input.target.id,
            subjectType,
            subjectId,
            action: 'read'
          });
        }),
        ...deletePermissions.map((permission) =>
          api.delete(`/api/permissions/${permission.id}`)
        )
      ]);
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: ['permissions', input.target.type, input.target.id]
      });
      close();
    },
    onError: (err: unknown) => {
      setPermissionsError(
        err instanceof ApiError ? err.message : 'Failed to update access'
      );
    }
  });

  const invite = useMutation({
    mutationFn: (body: { email: string; roomId: string }) =>
      api.post('/api/invites', body),
    onSuccess: () => {
      setInviteEmail('');
      setInviteError(null);
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: unknown) => {
      setInviteError(
        err instanceof ApiError ? err.message : 'Failed to send invite'
      );
    }
  });

  const loading = usersLoading || groupsLoading || permissionsLoading;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}
    >
      <DialogPanel aria-labelledby={titleId}>
        <h2
          id={titleId}
          className="text-lg font-semibold leading-none tracking-tight"
        >
          Share {target?.type === 'room' ? 'room' : 'folder'}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Invite people and manage who can view{' '}
          <span className="font-medium text-foreground">
            {target?.label ?? 'this item'}
          </span>
          .
        </p>

        <div className="mt-4 space-y-3 rounded-md border p-3">
          <label htmlFor="share-invite-email" className="text-sm font-medium">
            Invite by email
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="share-invite-email"
              type="email"
              autoComplete="email"
              placeholder="viewer@company.com"
              value={inviteEmail}
              disabled={invite.isPending}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                setInviteError(null);
              }}
            />
            <Button
              type="button"
              className="shrink-0"
              disabled={!inviteEmail.trim() || invite.isPending}
              onClick={() => {
                const email = normalizeEmail(inviteEmail);
                if (!email) return;
                invite.mutate({ email, roomId });
              }}
            >
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Invited people get access to this room. Use the list below for
            direct folder or room access.
          </p>
          {inviteError ? (
            <p className="text-sm text-destructive">{inviteError}</p>
          ) : null}
        </div>

        <form
          className="mt-4 space-y-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!target || updatePermissions.isPending) return;
            updatePermissions.mutate({
              target,
              selectedKeys: [...selectedSubjectKeys],
              existingPermissions: viewPermissions
            });
          }}
        >
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="max-h-80 space-y-4 overflow-y-auto rounded-md border p-3">
              <div className="space-y-2">
                <p className="text-sm font-medium">Users</p>
                {users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No users available.
                  </p>
                ) : (
                  users.map((user) => {
                    const key = permissionSubjectKey('user', user.id);
                    const isElevated = elevatedPermissionKeys.has(key);
                    const isInherited = inheritedPermissionKeys.has(key);
                    const detailLabel = permissionDetailLabel({
                      isElevated,
                      isInherited,
                      inheritedFrom:
                        inheritedPermissionsByKey.get(key)?.inheritedFrom
                    });
                    return (
                      <label
                        key={user.id}
                        className={cn(
                          'flex items-start gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent',
                          (isInherited || isElevated) &&
                            'cursor-not-allowed opacity-70'
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 size-4"
                          checked={selectedSubjectKeys.has(key)}
                          disabled={
                            isInherited ||
                            isElevated ||
                            updatePermissions.isPending
                          }
                          onChange={(e) => {
                            setSelectedSubjectKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(key);
                              else next.delete(key);
                              return next;
                            });
                          }}
                        />
                        <span className="min-w-0">
                          <span className="block truncate">{user.email}</span>
                          {detailLabel ? (
                            <span className="block text-xs text-muted-foreground">
                              {detailLabel}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Groups</p>
                {groups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {filterGroupsByRoomAccess
                      ? 'No groups have access to this room yet. Create a group under Settings → Groups, then grant access here.'
                      : 'No groups yet. Create one under Settings → Groups.'}
                  </p>
                ) : (
                  groups.map((group) => {
                    const key = permissionSubjectKey('group', group.id);
                    const isElevated = elevatedPermissionKeys.has(key);
                    const isInherited = inheritedPermissionKeys.has(key);
                    const detailLabel = permissionDetailLabel({
                      isElevated,
                      isInherited,
                      inheritedFrom:
                        inheritedPermissionsByKey.get(key)?.inheritedFrom
                    });
                    return (
                      <label
                        key={group.id}
                        className={cn(
                          'flex items-start gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent',
                          (isInherited || isElevated) &&
                            'cursor-not-allowed opacity-70'
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 size-4"
                          checked={selectedSubjectKeys.has(key)}
                          disabled={
                            isInherited ||
                            isElevated ||
                            updatePermissions.isPending
                          }
                          onChange={(e) => {
                            setSelectedSubjectKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(key);
                              else next.delete(key);
                              return next;
                            });
                          }}
                        />
                        <span className="min-w-0">
                          <span className="block truncate">{group.name}</span>
                          {detailLabel ? (
                            <span className="block text-xs text-muted-foreground">
                              {detailLabel}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            This only changes direct {target?.type ?? 'item'} access.
            {target?.type === 'folder'
              ? ' Room access and parent folder access still inherit normally.'
              : null}
          </p>
          {permissionsError ? (
            <p className="text-sm text-destructive">{permissionsError}</p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={updatePermissions.isPending}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updatePermissions.isPending || loading}
            >
              {updatePermissions.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}
